import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import {
  type NavigationContext,
  getViewState,
  isOnboardingIsolated,
  canOpenWindowDuringOnboarding,
} from '@aegislab/shared';
import { Sentry } from '../lib/sentry';
import { useAuthStore } from './authStore';
import { financeLedgerFiltersFromContext, useFinanceLedgerFilters } from './financeLedgerFilters';

export interface WindowPosition {
  x: number;
  y: number;
  width: number;
  height: number;
  viewport?: { width: number; height: number };
  /** 이 크기가 나온 프리셋 서명(`resolvePersistedBounds`). 없으면 스탬프 이전에 저장된 항목이다. */
  preset?: string;
}

export interface WindowState {
  openWindows: string[];
  focusedWindow: string | null;
  minimizedWindows: string[];
  zIndices: Record<string, number>;
  positions: Record<string, WindowPosition>;
  tabStates: Record<string, string>;
  zCounter: number;
  resizeVersion: number;
  navigationContext: NavigationContext | undefined;
}

export interface WindowActions {
  openWindow: (id: string, context?: NavigationContext) => void;
  closeWindow: (id: string) => void;
  focusWindow: (id: string) => void;
  minimizeWindow: (id: string) => void;
  restoreWindow: (id: string) => void;
  updatePosition: (id: string, pos: WindowPosition) => void;
  getStoredPosition: (id: string) => WindowPosition | null;
  setWindowTab: (windowId: string, tab: string | null) => void;
  getWindowTab: (windowId: string) => string | undefined;
  setNavigationContext: (context: NavigationContext | undefined) => void;
  pruneUnknownWindowIds: (knownIds: string[]) => void;
}

export type WindowStore = WindowState & WindowActions;

const SYNTHETIC_TAB_STATE_KEYS = new Set([
  'team-settings-view',
  'team-approvals',
  'team-retro-axis',
  'team-retro-campaign',
]);

const initialState: WindowState = {
  openWindows: ['attendance'],
  focusedWindow: 'attendance',
  minimizedWindows: [],
  zIndices: {},
  positions: {},
  tabStates: {},
  zCounter: 10,
  resizeVersion: 0,
  navigationContext: undefined,
};

function getViewport() {
  if (typeof document !== 'undefined') {
    const plane = document.getElementById('window-plane');
    if (plane && plane.clientWidth > 0 && plane.clientHeight > 0) {
      return { width: plane.clientWidth, height: plane.clientHeight };
    }
  }
  return { width: window.innerWidth, height: window.innerHeight };
}

function nextFocusedWindow(state: WindowState, closingId: string): string | null {
  const visible = state.openWindows.filter(
    (id) => id !== closingId && !state.minimizedWindows.includes(id)
  );
  const first = visible[0];
  if (!first) return null;
  return visible.reduce((best, id) => {
    const bestZ = state.zIndices[best] ?? 0;
    const z = state.zIndices[id] ?? 0;
    return z >= bestZ ? id : best;
  }, first);
}

function adjustPosition(pos: WindowPosition): WindowPosition {
  const vp = getViewport();
  const saved = pos.viewport;
  if (!saved || (saved.width === vp.width && saved.height === vp.height)) {
    return { ...pos, viewport: vp };
  }

  return {
    x: Math.round((pos.x / saved.width) * vp.width),
    y: Math.round((pos.y / saved.height) * vp.height),
    width: Math.min(pos.width, vp.width),
    height: Math.min(pos.height, vp.height),
    viewport: vp,
  };
}

function adjustAllPositions(
  positions: Record<string, WindowPosition>
): Record<string, WindowPosition> {
  const result: Record<string, WindowPosition> = {};
  for (const [id, pos] of Object.entries(positions)) {
    result[id] = adjustPosition(pos);
  }
  return result;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isValidPosition(v: unknown): v is WindowPosition {
  if (!isRecord(v)) return false;
  const nums = [v.x, v.y, v.width, v.height];
  if (!nums.every((n) => typeof n === 'number' && Number.isFinite(n))) return false;
  const vp = v.viewport;
  if (vp === undefined) return true;
  return (
    isRecord(vp) &&
    typeof vp.width === 'number' &&
    typeof vp.height === 'number' &&
    vp.width > 0 &&
    vp.height > 0
  );
}

/** 저장값에서 모양이 맞는 항목만 남긴다. 버린 것이 있으면 true 를 함께 돌려준다. */
function sanitizePersisted(persisted: unknown): {
  positions: Record<string, WindowPosition>;
  tabStates: Record<string, string>;
  dropped: boolean;
} {
  const raw = isRecord(persisted) ? persisted : {};
  let dropped = persisted !== undefined && persisted !== null && !isRecord(persisted);
  const positions: Record<string, WindowPosition> = {};
  if (raw.positions !== undefined) {
    if (isRecord(raw.positions)) {
      for (const [id, pos] of Object.entries(raw.positions)) {
        if (isValidPosition(pos)) positions[id] = pos;
        else dropped = true;
      }
    } else dropped = true;
  }
  const tabStates: Record<string, string> = {};
  if (raw.tabStates !== undefined) {
    if (isRecord(raw.tabStates)) {
      for (const [id, tab] of Object.entries(raw.tabStates)) {
        if (typeof tab === 'string') tabStates[id] = tab;
        else dropped = true;
      }
    } else dropped = true;
  }
  return { positions, tabStates, dropped };
}

let reportedCorruptStorage = false;

export const useWindowStore = create<WindowStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      openWindow: (id, context) =>
        set((state) => {
          // 온보딩 격리 (CP1 D1): 미완료 신규 사용자에겐 허용 창 밖 오픈을 전부 차단.
          // 독·헤더 탭·알림·딥링크·실시간 이벤트가 전부 이 단일 액션으로 수렴하므로 여기가 게이트다.
          // (initialState 의 attendance 는 이 액션을 거치지 않지만 허용 창이라 정합 — CP1 결정 문서 참조)
          if (
            isOnboardingIsolated(useAuthStore.getState().user) &&
            !canOpenWindowDuringOnboarding(id)
          ) {
            return {};
          }
          // 숨긴 캠페인 성과급 창은 옛 링크로도 열리지 않는다. 플래그 전체가 아니라 이 창만 막는 이유:
          // work-log 도 hidden 이지만 업무 일지 알림을 오늘 창으로 넘기는 창이라 열려야 한다.
          if (id === 'campaign-bonus' && getViewState(id) === 'hidden') return {};
          const alreadyOpen = state.openWindows.includes(id);
          const newZ = state.zCounter + 1;
          const ledgerFilters = id === 'finance' ? financeLedgerFiltersFromContext(context) : null;
          if (ledgerFilters) useFinanceLedgerFilters.getState().setFilters(ledgerFilters);
          const campaignBonusTab = id === 'campaign-bonus' ? context?.campaignBonusTab : undefined;
          return {
            openWindows: alreadyOpen ? state.openWindows : [...state.openWindows, id],
            minimizedWindows: state.minimizedWindows.filter((w) => w !== id),
            focusedWindow: id,
            zIndices: { ...state.zIndices, [id]: newZ },
            zCounter: newZ,
            ...(ledgerFilters ? { tabStates: { ...state.tabStates, finance: 'ledger' } } : {}),
            ...(campaignBonusTab
              ? { tabStates: { ...state.tabStates, 'campaign-bonus': campaignBonusTab } }
              : {}),
            ...(context !== undefined
              ? { navigationContext: { ...context, targetWindowId: id } }
              : {}),
          };
        }),

      closeWindow: (id) =>
        set((state) => ({
          openWindows: state.openWindows.filter((w) => w !== id),
          minimizedWindows: state.minimizedWindows.filter((w) => w !== id),
          focusedWindow:
            state.focusedWindow === id ? nextFocusedWindow(state, id) : state.focusedWindow,
        })),

      focusWindow: (id) =>
        set((state) => {
          const newZ = state.zCounter + 1;
          return {
            focusedWindow: id,
            zIndices: { ...state.zIndices, [id]: newZ },
            zCounter: newZ,
          };
        }),

      minimizeWindow: (id) =>
        set((state) => ({
          minimizedWindows: state.minimizedWindows.includes(id)
            ? state.minimizedWindows
            : [...state.minimizedWindows, id],
          focusedWindow:
            state.focusedWindow === id ? nextFocusedWindow(state, id) : state.focusedWindow,
        })),

      restoreWindow: (id) =>
        set((state) => {
          const newZ = state.zCounter + 1;
          return {
            minimizedWindows: state.minimizedWindows.filter((w) => w !== id),
            focusedWindow: id,
            zIndices: { ...state.zIndices, [id]: newZ },
            zCounter: newZ,
          };
        }),

      updatePosition: (id, pos) =>
        set((state) => ({
          positions: {
            ...state.positions,
            [id]: { ...pos, viewport: getViewport() },
          },
        })),

      getStoredPosition: (id): WindowPosition | null => {
        const pos = get().positions[id];
        return pos ?? null;
      },

      setWindowTab: (windowId, tab) =>
        set((state) => {
          if (tab === null) {
            const { [windowId]: _removed, ...rest } = state.tabStates;
            return { tabStates: rest };
          }
          return { tabStates: { ...state.tabStates, [windowId]: tab } };
        }),

      getWindowTab: (windowId) => {
        return get().tabStates[windowId];
      },

      setNavigationContext: (context) => set({ navigationContext: context }),

      pruneUnknownWindowIds: (knownIds) =>
        set((state) => {
          const known = new Set(knownIds);
          const positionIds = Object.keys(state.positions);
          const tabIds = Object.keys(state.tabStates);
          const staleInPositions = positionIds.some((id) => !known.has(id));
          const staleInTabs = tabIds.some(
            (id) => !known.has(id) && !SYNTHETIC_TAB_STATE_KEYS.has(id)
          );
          if (!staleInPositions && !staleInTabs) return {};

          const nextPositions: Record<string, WindowPosition> = {};
          for (const [id, pos] of Object.entries(state.positions)) {
            if (known.has(id)) nextPositions[id] = pos;
          }
          const nextTabs: Record<string, string> = {};
          for (const [id, tab] of Object.entries(state.tabStates)) {
            if (known.has(id) || SYNTHETIC_TAB_STATE_KEYS.has(id)) nextTabs[id] = tab;
          }
          return { positions: nextPositions, tabStates: nextTabs };
        }),
    }),
    {
      name: 'aegislab-windows',
      storage: createJSONStorage(() => localStorage),
      version: 1,
      partialize: (state) => ({
        positions: state.positions,
        tabStates: state.tabStates,
      }),
      // v0(버전 필드 없음) → v1 마이그레이션.
      // partialize 확장(minimizedWindows/zIndices 등) 시 이전 localStorage 데이터와
      // 충돌하면 앱 시작 실패 가능 — migrate가 정규화하여 graceful fallback.
      // Zustand v5 실행 순서: migrate(version 불일치 시) → merge → adjustAllPositions.
      migrate: (persisted, version) => {
        const raw = persisted as Record<string, unknown> | null | undefined;
        // v0 또는 알 수 없는 버전: positions/tabStates 형태 검증 후 통과.
        if (version === 0 || version === undefined) {
          const positions = raw?.positions;
          const tabStates = raw?.tabStates;
          return {
            positions:
              positions && typeof positions === 'object' && !Array.isArray(positions)
                ? (positions as Record<string, WindowPosition>)
                : {},
            tabStates:
              tabStates && typeof tabStates === 'object' && !Array.isArray(tabStates)
                ? (tabStates as Record<string, string>)
                : {},
          };
        }
        // v1+는 그대로 통과 — merge 가 형태를 보증한다.
        return raw ?? {};
      },
      merge: (persisted, current) => {
        const { positions, tabStates, dropped } = sanitizePersisted(persisted);
        if (dropped && !reportedCorruptStorage) {
          reportedCorruptStorage = true;
          Sentry.captureMessage('windowStore: 깨진 저장값을 기본값으로 되돌림', 'warning');
        }
        return {
          ...current,
          positions: adjustAllPositions(positions),
          tabStates,
        };
      },
      onRehydrateStorage: () => (_state, error) => {
        if (error && !reportedCorruptStorage) {
          reportedCorruptStorage = true;
          Sentry.captureException(error);
        }
      },
    }
  )
);

/**
 * 격리 발동 시 이미 열려 있던 비허용 창 정리 (CP1 D1 보강 — CP2 리뷰).
 * openWindow 게이트는 신규 오픈만 막으므로, 같은 탭 로그아웃→격리 대상 로그인이나
 * 게이트-오프 틈에 열린 창은 이 정리 경로가 닫는다. MainPage 가 격리 발동 시 호출한다.
 */
export function purgeBlockedWindowsDuringOnboarding(): void {
  if (!isOnboardingIsolated(useAuthStore.getState().user)) return;
  const state = useWindowStore.getState();
  for (const id of [...state.openWindows]) {
    if (!canOpenWindowDuringOnboarding(id)) state.closeWindow(id);
  }
}

// 뷰포트 변경 시 윈도우 위치 비율 보정 + 리마운트 트리거
if (typeof window !== 'undefined') {
  let resizeTimer: ReturnType<typeof setTimeout> | null = null;
  window.addEventListener('resize', () => {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      useWindowStore.setState((s) => ({
        positions: adjustAllPositions(s.positions),
        resizeVersion: s.resizeVersion + 1,
      }));
    }, 120);
  });
}

/**
 * Selector factory: returns a number (primitive) for a specific window's z-index.
 * Because the return is a primitive, Zustand's Object.is check will skip re-renders
 * when other windows' z-indices change.
 */
export const selectZIndex =
  (id: string) =>
  (state: WindowStore): number =>
    state.zIndices[id] ?? 0;
